<template>
  <b-modal
      v-model="showModal"
      size="lg"
      id="units-modal"
      :title="editedUnit.id > 0 ? 'Edit ' + (companyPurpose !== 'rent' ? 'product' : 'unit') + ' details' : 'Add new ' + (companyPurpose !== 'rent' ? 'product' : 'unit') "
      ok-title="Save"
      cancel-variant="outline-secondary"
      @ok.prevent="onSubmit"
      :ok-disabled="isFormLoading"
      :cancel-disabled="isFormLoading"
  >
    <!-- Add this loading overlay -->
    <div v-if="isFormLoading" class="modal-overlay d-flex align-items-center justify-content-center">
      <b-spinner variant="primary" label="Loading..."></b-spinner>
    </div>

    <div v-if="isContentLoading" class="text-center pt-3">
      <b-spinner label="Loading..." />
    </div>
    <b-form v-else>
      <b-row v-if="editedUnit.platform ?? false" class="pb-1">
        <b-col cols="2">
          <img v-if="editedUnit.platform.picture ?? false"
               :src="editedUnit.platform.picture"
               :style="{ maxWidth: '100px' }"
          />
        </b-col>
        <b-col cols="10" class="pt-2">
          <a target="_blank" :href="editedUnit.platform.url">{{ editedUnit.platform.name }} - {{ editedUnit.platform.type }}</a>
          <br>
          <a target="_blank"
             :href="editedUnit.platform.url + (editedUnit.platform.type == 'WooCommerce' ? 'wp-admin/post.php?post=' + editedUnit.id_on_platform + '&action=edit' : '')"
          ><b-button variant="outline-secondary" class="my-1 not-very-small">Check on {{editedUnit.platform.type}} #{{ editedUnit.id_on_platform }}</b-button></a>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="12">
          <b-form-group
              label="Name"
              label-for="name"
          >
            <b-form-input
                v-model="editedUnit.name"
                placeholder="Name"
            />
            <small class="text-danger" v-if="v$.editedUnit.name.$error">Name is required</small>
          </b-form-group>
          <b-form-group
              label="Description"
              label-for="description"
          >
            <b-form-textarea
                v-model="editedUnit.description"
                placeholder="Description"
                rows="3"
                max-rows="6"
            />

          </b-form-group>
          <b-form-group
              label="Pictures"
              label-for="picture"
          >
            <b-form-file
                multiple="multiple"
                v-model="editedUnit.picture"
                placeholder="Choose the files or drop them here..."
                drop-placeholder="Drop the files here..."
                v-if="true /*editedUnit.id_on_platform == null*/"
                class="mb-1"
            />
            <div v-else class="alert-warning p-1">Pictures uploading for imported items is under development</div>
            <div class="d-flex flex-wrap">
              <div
                v-if="editedUnit.picture_preview !== null"
                v-for="pic in editedUnit.picture_preview"
                class="unit-image-edit-container"
              >
                <div class="unit-image-delete-container">
                  <b-button
                      variant="primary"
                      class="mx-0 float-right"
                      size="sm"
                      @click="deleteImage(pic)"
                  >
                    <span>{{ isImageInDeleteArray(pic.src) ? 'Restore' : 'X' }}</span>
                  </b-button>
                </div>
                <img
                    :src="pic.src"
                    :style="{ width: '150px', border: '5px solid #DDDDDD' }" 
                    :class="'my-1 mx-1 ' + (isImageInDeleteArray(pic.src) ? 'img-transparent' : '') "
                />
              </div>
            </div>
          </b-form-group>

        </b-col>
      </b-row>

      <b-row v-if="companyPurpose === 'rent'">
        <b-col cols="6">
          <b-form-group label="Maintenance from date" label-for="input-name">
            <b-form-datepicker
                id="maintenance_from"
                :min="minDate"
                v-model="editedUnit.maintenance_from"
                locale="en"
            />
          </b-form-group>

          <b-form-group label="Maintenance to date" label-for="input-name">
            <b-form-datepicker
                id="maintenance_to"
                :min="minDate"
                v-model="editedUnit.maintenance_to"
                locale="en"
                @context="onContext"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="6">
          <b-form-group :label="priceLabel" label-for="price">
            <b-form-input
                v-model="editedUnit.price"
                :placeholder="priceLabel"
                type="number"
            />
            <small class="text-danger" v-if="v$.editedUnit.price.$error">Price is numeric</small>
          </b-form-group>
        </b-col>
        <b-col cols="6" v-if="companyPurpose !== 'rent'">
          <b-form-group :label="'SKU'" label-for="sku">
            <b-form-input
                v-model="editedUnit.sku"
                :placeholder="'SKU'"
                type="text"
            />
            <small class="text-danger" v-if="v$.editedUnit.sku != undefined && v$.editedUnit.sku.$error">SKU must have at least 3 characters</small>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="6">
          <b-form-group :label="'Items contains'" label-for="items_quantity">
            <b-form-input
                v-model="editedUnit.items_quantity"
                type="number"
                min="1"
                step="1"
            />
            <small class="text-danger" v-if="v$.editedUnit.items_quantity.$error">Quantity is numeric and min. 1</small>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row v-if="companyPurpose !== 'rent'">
        <b-col :cols="canManageCustomBarcodes ? 6 : 12">
          <b-form-group label="Barcode" label-for="barcode">
            <BarcodeTargetField
              hint-key="product-barcode"
              :active="showModal"
            >
              <b-form-input
                  id="barcode"
                  class="barcode-target"
                  v-model="editedUnit.barcode"
                  placeholder="International product barcode"
                  type="text"
                  maxlength="100"
                  :state="barcodeError ? false : null"
                  @input="barcodeError = ''"
              />
            </BarcodeTargetField>
            <small class="text-danger" v-if="barcodeError">{{ barcodeError }}</small>
          </b-form-group>
        </b-col>
        <b-col
          v-if="canManageCustomBarcodes"
          cols="6"
          class="d-flex align-items-end"
        >
          <b-form-group
            label="Custom barcodes"
            label-for="custom-barcodes-btn"
            class="w-100"
          >
            <b-button
              id="custom-barcodes-btn"
              class="custom-barcodes-btn"
              variant="outline-primary"
              block
              @click="openCustomBarcodesModal"
            >
              Custom barcodes{{ customBarcodes.length ? ` (${customBarcodes.length})` : '' }}
            </b-button>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12">
          <b-form-group label="Category" label-for="select-unit-category">
            <v-select
                id="select-unit-category"
                v-model="editedUnit.unit_category_id"
                placeholder="Select category"
                item-value="id"
                item-text="name"
                label="name"
                :options="unitCategories"
                :clearable="true"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row v-if="platforms.length > 0 && editedUnit.id_on_platform == null">
        <b-col cols="12">
          <b-form-group :label="editedUnit.connected_platforms && editedUnit.connected_platforms.length > 0 ? 'Available on platforms' : 'Platforms'" label-for="select-platform">
            <ul v-if="editedUnit.connected_platforms && editedUnit.connected_platforms.length > 0" class="connected-platforms-list connected-platforms-list-manage-unit">
              <li v-for="connectedPlatform in editedUnit.connected_platforms" class="d-flex align-items-center">
                <img v-if="connectedPlatform.picture ?? false"
                     :src="connectedPlatform.picture"
                />
                <a target="_blank" :href="connectedPlatform.url + '/product/' + connectedPlatform.pivot.slug">{{ connectedPlatform.name }} ({{ connectedPlatform.type }})</a>
              </li>
            </ul>
            <v-select v-else
                id="select-platform"
                v-model="editedUnit.platforms_ids"
                placeholder="Select platforms to import the item"
                item-value="id"
                item-text="name"
                :get-option-label="platform => `${platform.name} (${platform.type})`"
                :options="platforms"
                multiple="multiple"
                :clearable="true"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row v-if="companyPurpose !== 'rent'">
        <b-col cols="12">
          <b-form-group
              v-show="!hideStockManagement"
              label=""
              label-for="stock_management"
          >
            <b-form-checkbox v-model="editedUnit.stock_management" switch class="mb-1">
              Enable stock management
            </b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row v-if="companyPurpose !== 'rent' && editedUnit.stock_management">
        <b-col cols="12">
          <b-form-group label="" label-for="allow_backorder">
            <b-form-checkbox v-model="editedUnit.allow_backorder" switch class="mb-1">
              Allow backorder
            </b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>
    </b-form>

  </b-modal>
  <ManageCustomBarcodes
    v-if="companyPurpose !== 'rent'"
    :click-date="customBarcodesClickDate"
    :unit-id="editedUnit.id"
    :initial-barcodes="customBarcodes"
    @saved="onCustomBarcodesSaved"
  />
</template>
<script setup>
import { watch, onMounted, ref, computed, defineComponent, reactive, inject, getCurrentInstance, nextTick } from 'vue';
import {mapActions, mapGetters, useStore} from "vuex";
import vSelect from 'vue-select';
import {
  BButton, BCol, BDropdownItem,
  BForm,
  BFormCheckbox,
  BFormDatepicker, BFormFile,
  BFormGroup,
  BFormInput,
  BFormTextarea, BModal, BRow,
  BSpinner,
  VBModal
} from "bootstrap-vue";
import useVuelidate from '@vuelidate/core';
import {email, integer, minLength, minValue, numeric, required} from "@vuelidate/validators";
import {sweetError, sweetWarning} from "@/libs/sweet-alerts";
//import PhoneMaskInput from 'vue-phone-mask-input';
import { toastSuccess, toastFailure } from '@/libs/toastification';
import {backendErrorString} from "@/libs/utils";
import { ORDER_PRODUCT_ADD_KEY } from '@/constants/injectionKeys';
import { canStaffChange, canStaffCreate } from '@/libs/staffPermissions';
import ManageCustomBarcodes from '@/views/Components/ManageCustomBarcodes.vue';
import BarcodeTargetField from '@/views/Components/Elements/BarcodeTargetField.vue';

const cloneObject = (object) => {
  return JSON.parse(JSON.stringify(object))
}

const companyPurpose = ref(window.companyPurpose);
const minDate = new Date();

const defaultUnit = {
  id: null,
  name: null,
  description: null,
  picture: null,
  picture_preview: null,
  maintenance_from: null,
  maintenance_to: null,
  sku: null,
  barcode: null,
  price: null,
  items_quantity: 1,
  stock_management: false,
  allow_backorder: false,
  unit_category_id: null,
  platforms_ids: null,
  platform: null,
  connected_platforms: null,
  id_on_platform: null
};

const defaultUnitCategory = {
  id: null,
  name: null,
  description: null,
  picture: null,
};

const props = defineProps({
  unit: {
    type: Object,
    required: true
  },
  clickDate: {
    type: Date,
    required: true
  },
  defaultStockManagement: {
    type: Boolean,
    default: false,
  },
  hideStockManagement: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['unit-created']);

const selectOrderProduct = inject(ORDER_PRODUCT_ADD_KEY, null);

const notifyUnitCreated = (createdUnit) => {
  const unit = createdUnit?.data ?? createdUnit;

  if (!unit?.id) {
    return;
  }

  store.commit('units/SET_ORDER_CREATED_PRODUCT', unit);
  emit('unit-created', createdUnit);

  if (typeof selectOrderProduct === 'function') {
    selectOrderProduct(createdUnit);
  }
};

const showModal = ref(false);
const isContentLoading = ref(true);
const isFormLoading = ref(false);
const barcodeError = ref('');
const customBarcodes = ref([]);
const customBarcodesClickDate = ref(null);

const store = useStore();
const unit = computed(() => store.state.units.unit);
const authUser = computed(() => store.getters['auth/user']);
const companySettings = computed(() => store.getters['companies/companySettings']);

const priceLabel = computed(() => {
  const symbol = companySettings.value?.currency_symbol || window.currency_symbol || '$'
  const base = `Price (${symbol})`
  return companyPurpose.value === 'rent' ? `${base} per hour` : base
});

let temporaryPicture = null;
let editedUnit = reactive(cloneObject(defaultUnit));
let unitCategories = reactive([{}, {}]);
let platforms = reactive([{}, {}]);

const canManageCustomBarcodes = computed(() => {
  if (editedUnit.id) {
    return canStaffChange(authUser.value, 'products');
  }
  return canStaffCreate(authUser.value, 'products');
});

const imagesToDelete = ref([]);

const rules = {
  editedUnit: {
    name: {required, minLength: minLength(3)},
    price: {required, numeric},
    items_quantity: {integer, minValue: minValue(1)}
  }
};

const v$ = useVuelidate(rules,{ editedUnit });

const applyBackendFieldErrors = (error) => {
  barcodeError.value = '';
  const data = error?.response?.data;

  if (data?.barcode) {
    barcodeError.value = Array.isArray(data.barcode) ? data.barcode[0] : data.barcode;
  }

  return backendErrorString(error);
};

function onContext(ctx) {
  this.formatted = ctx.selectedFormatted;
  this.selected = ctx.selectedYMD;
}

const mapCustomBarcodes = (list) => {
  if (!Array.isArray(list)) {
    return [];
  }

  return list
    .map((item) => ({
      id: item?.id ?? null,
      barcode: String(item?.barcode ?? '').trim(),
      description: item?.description != null ? String(item.description).trim() : '',
    }))
    .filter((item) => item.barcode !== '');
};

watch(unit, (newUnit) => {

  if (newUnit.data && newUnit.data.id > 0) {

    editedUnit.id = newUnit.data.id;
    editedUnit.name = newUnit.data.name;
    editedUnit.price = newUnit.data.price;
    editedUnit.sku = newUnit.data.sku;
    editedUnit.barcode = newUnit.data.barcode;
    editedUnit.items_quantity = parseInt(newUnit.data.items_quantity) <= 0 ? 1 : parseInt(newUnit.data.items_quantity);
    editedUnit.description = newUnit.data.description;
    editedUnit.picture_preview = newUnit.data.picture;
    editedUnit.maintenance_from = newUnit.data.maintenance_from;
    editedUnit.maintenance_to = newUnit.data.maintenance_to;
    editedUnit.platform = newUnit.data.platform;
    editedUnit.connected_platforms = newUnit.data.connected_platforms;
    editedUnit.id_on_platform = newUnit.data.id_on_platform;
    editedUnit.stock_management = Boolean(newUnit.data.stock_management);
    editedUnit.allow_backorder = Boolean(newUnit.data.allow_backorder);
    customBarcodes.value = mapCustomBarcodes(newUnit.data.custom_barcodes);
  } else {

    Object.assign(editedUnit, cloneObject(defaultUnit));
    customBarcodes.value = [];
  }

  imagesToDelete.value = [];
  barcodeError.value = '';
});

const openCustomBarcodesModal = () => {
  customBarcodesClickDate.value = new Date();
};

const onCustomBarcodesSaved = (list) => {
  customBarcodes.value = mapCustomBarcodes(list);
};

watch(
  () => editedUnit.stock_management,
  (enabled) => {
    if (!enabled) {
      editedUnit.allow_backorder = false;
    }
  }
);

const focusBarcodeField = () => {
  if (companyPurpose.value === 'rent') {
    return
  }

  nextTick(() => {
    const input = document.getElementById('barcode')
    input?.focus()
    input?.select?.()
  })
}

watch(showModal, (open) => {
  if (open && !isContentLoading.value) {
    focusBarcodeField()
  }
})

watch(isContentLoading, (loading) => {
  if (!loading && showModal.value) {
    focusBarcodeField()
  }
})

watch(
    () => props.clickDate,
    (newVal) => {

      isContentLoading.value = true;
      customBarcodes.value = [];
      barcodeError.value = '';

      if (props.unit !== null ) {

        const unitId = props.unit && props.unit.id;
        store.dispatch('units/loadUnit', unitId)
            .then(() => store.dispatch('unitCategories/loadUnitCategories'))
            .then(() => store.dispatch('platforms/loadPlatforms'))
            .then(() => {

              isContentLoading.value = false;
              showModal.value = true;

              unitCategories = store.state.unitCategories.unitCategories;
              editedUnit.unit_category_id = unitCategories.find(p => p.id === props.unit.unit_category_id);

              platforms = store.state.platforms.platforms;
            })
            .catch((error) => {
              isContentLoading.value = false;
              showModal.value = false;
              toastFailure('Something went wrong: ' + error.message);
            });
      } else {

        Object.assign(editedUnit, cloneObject(defaultUnit));
        customBarcodes.value = [];
        if (props.hideStockManagement) {
          editedUnit.stock_management = false;
          editedUnit.allow_backorder = false;
        } else if (props.defaultStockManagement) {
          editedUnit.stock_management = true;
        }
        store.dispatch('unitCategories/loadUnitCategories')
            .then(() => store.dispatch('platforms/loadPlatforms'))
            .then(() => {
              isContentLoading.value = false;
              showModal.value = true;

              unitCategories = store.state.unitCategories.unitCategories;
              platforms = store.state.platforms.platforms;

            }).catch((error) => {
              isContentLoading.value = false;
              showModal.value = false;
              toastFailure('Something went wrong: ' + error.message);
            });
      }
    }
);


const onSubmit = async (event) => {

  event.preventDefault();
  const isFormCorrect = await v$.value.$validate();
  if (!isFormCorrect) {
    console.log('Validation failed');
    return;
  } else {
    console.log('Validation true');
  }

  await submitForm();
};

const getTemporaryPicture = async () => {
  return Promise.all(Array.from(editedUnit.picture).map(file => {
    return new Promise(resolve => {
      const reader = new FileReader()

      reader.addEventListener('load', () => {
        resolve(reader.result)
      }, false)

      if (/\.(jpe?g|png|gif)$/i.test(file.name)) {
        reader.readAsDataURL(file)
      } else {
        resolve(null)
      }
    })
  }))
}

const deleteImage = (img) => {
  const index = imagesToDelete.value.indexOf(img.src);
  if (index > -1) {
    // Remove if exists
    imagesToDelete.value.splice(index, 1);
  } else {
    // Add if doesn't exist
    imagesToDelete.value.push(img.src);
  }
};

const isImageInDeleteArray = (imgSrc) => {
  return imagesToDelete.value.includes(imgSrc);
};


// Then in submitForm, modify to handle multiple files
const submitForm = async () => {
  isFormLoading.value = true;
  const formData = new FormData();
  const skipKeys = new Set([
    'picture',
    'picture_preview',
    'platform',
    'connected_platforms',
    'platforms_ids',
    'unit_category_id',
    'custom_barcodes',
  ]);

  for (const [key, value] of Object.entries(editedUnit)) {
    if (value === null || value === undefined || skipKeys.has(key)) {
      continue;
    }
    if (typeof value === 'object') {
      continue;
    }
    formData.append(key, value);
  }

  if (editedUnit.picture && editedUnit.picture.length) {
    Array.from(editedUnit.picture).forEach((file) => {
      if (file instanceof File) {
        formData.append('picture[]', file);
      }
    });
    await getTemporaryPicture();
  }

  if (editedUnit.platforms_ids && editedUnit.platforms_ids.length) {
    Array.from(editedUnit.platforms_ids).forEach((platform) => {
      formData.append('platforms[]', platform.id);
    });
  }

  formData.append(
    'unit_category_id',
    editedUnit.unit_category_id ? editedUnit.unit_category_id.id : 0,
  );

  imagesToDelete.value.forEach((image) => {
    formData.append('pictures_to_delete[]', image);
  });

  if (companyPurpose.value !== 'rent') {
    formData.set('barcode', editedUnit.barcode ?? '');
  }

  if (companyPurpose.value !== 'rent' && editedUnit.id == null) {
    formData.set(
      'custom_barcodes',
      JSON.stringify(
        customBarcodes.value.map((item) => ({
          barcode: item.barcode,
          description: item.description || null,
        })),
      ),
    );
  }

  if (editedUnit.id != null) {

    formData.append('_method', 'PUT');

    store.dispatch('units/updateUnit', {formData})
        .then(() => {
          showModal.value = false;
          toastSuccess('Successfully updated');
        })
        .catch((error) => {

          const errorMessages = applyBackendFieldErrors(error);
          toastFailure(errorMessages);
        })
        .finally(() => {
          isFormLoading.value = false;
        });
  } else {

    formData.append('_method', 'POST');
    store.dispatch('units/addUnit', {formData})
        .then((createdUnit) => {
          showModal.value = false;
          toastSuccess('Successfully added');
          notifyUnitCreated(createdUnit);
        })
        .catch((error) => {

          const errorMessages = applyBackendFieldErrors(error);
          toastFailure(errorMessages);
        })
        .finally(() => {
          isFormLoading.value = false;
        });
  }

  editedUnit.picture = null;
  temporaryPicture = null;

};

</script>
<style scoped>
@media (max-width: 767.98px) {
  .custom-barcodes-btn {
    font-size: 0.75rem;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
}
</style>
